import React from "react";

import { Button, Input, Popover, Space, Spin, Table, Tag, Tooltip } from "antd";
import {
  ApiOutlined,
  AreaChartOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  CopyOutlined,
  DesktopOutlined,
  ExportOutlined,
  LinkOutlined,
  SettingOutlined,
  ToolOutlined,
} from "@ant-design/icons";

import { services } from "./services";
import { stages } from "./stages";

const serviceTypeIcons = {
  BE: <CodeOutlined />,
  FE: <DesktopOutlined />,
  SP: <ToolOutlined />,
};

const serviceTypeLabels = {
  BE: "Backend",
  FE: "Frontend",
  SP: "Support",
};

const columns = [
  {
    title: "Service Name",
    dataIndex: "name",
    key: "name",
    width: 360,
  },
  {
    title: (
      <span>
        {"Development"}
        <Tooltip title="Kubernetes Dashboard">
          <SettingOutlined
            className="dashboard-setting-icon"
            onClick={() =>
              window.open(
                "https://dashboard.development.languages.akelius.com/#!/overview?namespace=business-school-development",
                "_blank",
              )
            }
          />
        </Tooltip>
      </span>
    ),
    dataIndex: "development",
    key: "development",
  },
  {
    title: (
      <span>
        {"Testing"}
        <Tooltip title="Kubernetes Dashboard">
          <SettingOutlined
            className="dashboard-setting-icon"
            onClick={() =>
              window.open(
                "https://dashboard.development.languages.akelius.com/#!/overview?namespace=business-school-testing",
                "_blank",
              )
            }
          />
        </Tooltip>
      </span>
    ),
    dataIndex: "testing",
    key: "testing",
  },
  {
    title: (
      <span>
        {"Production"}
        <Tooltip title="Kubernetes Dashboard">
          <SettingOutlined
            className="dashboard-setting-icon"
            onClick={() =>
              window.open(
                "https://dashboard.production.languages.akelius.com/#!/overview?namespace=business-school-production",
                "_blank",
              )
            }
          />
        </Tooltip>
      </span>
    ),
    dataIndex: "production",
    key: "production",
  },
];

const renderStatus = (service, idx) => ({
  key: `service_${idx}`,
  name: (
    <div className="service-name">
      <Tooltip title={serviceTypeLabels[service.type]}>
        <span
          role="img"
          className={`service-type service-type--${service.type.toLowerCase()}`}
          aria-label={serviceTypeLabels[service.type]}
        >
          {serviceTypeIcons[service.type]}
        </span>
      </Tooltip>
      <h3>{service.name}</h3>
    </div>
  ),
  development: (
    <Status
      status={service.development.status}
      link={service.link}
      git={service.git}
      url={service.development.url}
      swagger={service.swagger}
      report={service.report}
      commit={service.development.commit}
      timestamp={service.development.timestamp}
      loading={service.development.loading}
    />
  ),
  testing: (
    <Status
      status={service.testing.status}
      link={service.link}
      git={service.git}
      url={service.testing.url}
      swagger={service.swagger}
      report={service.report}
      commit={service.testing.commit}
      timestamp={service.testing.timestamp}
      loading={service.testing.loading}
    />
  ),
  production: (
    <Status
      status={service.production.status}
      link={service.link}
      git={service.git}
      url={service.production.url}
      swagger={service.swagger}
      report={service.report}
      commit={service.production.commit}
      timestamp={service.production.timestamp}
      loading={service.production.loading}
    />
  ),
});

const templateStatus = {
  status: "unknown",
  commit: null,
  timestamp: null,
  loading: false,
};

class StatusTable extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      services: services.map((service, idx) => ({
        type: service.type,
        name: service.name,
        link: service.link,
        git: service.git,
        swagger: service.swagger,
        report: service.report,
        development: Object.assign(
          { url: service.development },
          templateStatus,
        ),
        testing: Object.assign({ url: service.testing }, templateStatus),
        production: Object.assign({ url: service.production }, templateStatus),
      })),
    };
    this.getStatus = (service, idx) => {
      Object.keys(service)
        .filter((key) => stages.includes(key))
        .forEach((key) => {
          const services = [...this.state.services];
          services[idx] = Object.assign(services[idx], {
            [key]: Object.assign(services[idx][key], { loading: true }),
          });
          this.setState({ services: services });
          if (service.info) {
            fetch(service[key] + service.info + "?v=" + Date.now(), {
              headers: {
                "Content-Type": "application/json; charset=utf-8",
              },
            }).then(
              (res) => {
                res.json().then((json) => {
                  if (json.git) {
                    const services = [...this.state.services];
                    services[idx] = Object.assign(services[idx], {
                      [key]: {
                        url: services[idx][key].url,
                        status: "up",
                        commit: json.git.commit.id,
                        timestamp: json.git.commit.time,
                        loading: false,
                      },
                    });
                    this.setState({ services: services });
                  }
                });
              },
              (res) => {
                const services = [...this.state.services];
                services[idx] = Object.assign(services[idx], {
                  [key]: {
                    status: "down",
                    commit: null,
                    timestamp: null,
                    loading: false,
                  },
                });
                this.setState({ services: services });
              },
            );
          } else {
            services[idx] = Object.assign(services[idx], {
              [key]: {
                url: services[idx][key].url,
                status: "up",
                commit: "none",
                timestamp: Date.now(),
                loading: false,
              },
            });
          }
        });
    };
  }

  componentDidMount() {
    services.forEach((service, idx) => {
      this.getStatus(service, idx);
      setInterval(() => {
        this.getStatus(service, idx);
      }, 15000);
    });
  }

  render() {
    const { serviceFilter = "all" } = this.props;
    const searchQuery = (this.props.serviceSearch || "").trim().toLowerCase();
    const visibleStages = this.props.visibleStages || stages;
    const visibleServices =
      serviceFilter === "all"
        ? this.state.services
        : this.state.services.filter(
            (service) => service.type === serviceFilter,
          );
    const searchFilteredServices = visibleServices.filter((service) =>
      service.name.toLowerCase().includes(searchQuery),
    );

    return (
      <Table
        dataSource={searchFilteredServices.map(renderStatus)}
        columns={columns.filter(
          (column) =>
            column.key === "name" || visibleStages.includes(column.key),
        )}
        pagination={false}
        sticky={{ offsetHeader: 64 }}
      />
    );
  }
}

class Status extends React.Component {
  state = {
    linkPopoverVisible: false,
  };

  openPopover = () => {
    this.setState({
      linkPopoverVisible: true,
    });
  };

  closePopover = (linkPopoverVisible) => {
    this.setState({ linkPopoverVisible });
  };

  urlRef = React.createRef();

  copyToClipboard = () => {
    this.urlRef.current.input.select();
    document.execCommand("copy");
  };

  getContent = (url) => {
    return (
      <Space.Compact className="copy-url-input">
        <Input ref={this.urlRef} size="large" value={url} readOnly />
        <Tooltip title="Copy URL">
          <Button
            aria-label="Copy URL"
            icon={<CopyOutlined />}
            onClick={this.copyToClipboard}
            size="large"
          />
        </Tooltip>
      </Space.Compact>
    );
  };

  header = (<h3 className="copy-url-title">{"Copy URL"}</h3>);

  render() {
    return (
      <div className="status-cell">
        <div className="status-commit-container">
          <span className="status-indicator-slot">
            {this.props.loading ? (
              <Spin className="status-loading" />
            ) : (
              <Tooltip title={this.props.status}>
                <span
                  role="img"
                  className={`status-indicator status-indicator--${this.props.status}`}
                  aria-label={this.props.status}
                />
              </Tooltip>
            )}
          </span>
          {this.props.commit ? (
            <Tag
              variant="outlined"
              className="status-commit service-status-commit"
              onClick={() =>
                this.props.commit
                  ? window.open(this.props.git + this.props.commit, "_blank")
                  : null
              }
            >
              {this.props.commit}
            </Tag>
          ) : (
            <span className="service-status-unknown">—</span>
          )}
        </div>
        <span className="status-action-slot">
          {this.props.timestamp && (
            <Tooltip title={this.props.timestamp}>
              <ClockCircleOutlined className="status-action-icon" />
            </Tooltip>
          )}
        </span>

        <span className="status-action-slot">
          {this.props.swagger && this.props.status === "up" ? (
            <Tooltip title="Swagger">
              <ApiOutlined
                className="status-action-icon"
                onClick={() =>
                  this.props.swagger
                    ? window.open(this.props.url + this.props.swagger, "_blank")
                    : null
                }
              />
            </Tooltip>
          ) : this.props.report ? (
            <Tooltip title="Report">
              <AreaChartOutlined
                className="status-action-icon"
                onClick={() =>
                  this.props.report
                    ? window.open(this.props.report, "_blank")
                    : null
                }
              />
            </Tooltip>
          ) : null}
        </span>
        <span className="status-action-slot">
          {this.props.url ? (
            <>
              <Tooltip title="Copy URL">
                <ExportOutlined
                  className="status-action-icon"
                  onClick={this.openPopover}
                />
              </Tooltip>
              <Popover
                open={this.state.linkPopoverVisible}
                trigger="click"
                title={this.header}
                content={this.getContent(this.props.url)}
                onOpenChange={this.closePopover}
              />
            </>
          ) : null}
        </span>

        <span className="status-action-slot">
          {this.props.link && this.props.url ? (
            <Tooltip title="Open service">
              <a
                href={this.props.url}
                target="_blank"
                rel="noreferrer"
                className="status-action-icon"
              >
                <LinkOutlined />
              </a>
            </Tooltip>
          ) : null}
        </span>
      </div>
    );
  }
}

export { StatusTable };
